import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import { Domain, UpdateDomainRequest, DomainType } from '../../../types';
import {
  useDomainGameMappings,
  useGameProviders,
  useReplaceDomainGameMappings,
  useUpdateDomain,
} from '../../../hooks/useApi';

interface UpdateDomainModalProps {
  domain: Domain | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const UpdateDomainModal: React.FC<UpdateDomainModalProps> = ({ domain, isOpen, onClose, onSuccess }) => {
  const [form, setForm] = useState<UpdateDomainRequest>({});
  const [ipInput, setIpInput] = useState('');
  const [useWhitelist, setUseWhitelist] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [showAdvancedNetwork, setShowAdvancedNetwork] = useState(false);
  const [gameMode, setGameMode] = useState<'all' | 'selected'>('all');

  const updateDomain = useUpdateDomain();
  const replaceMappings = useReplaceDomainGameMappings();
  const { data: mappingsData } = useDomainGameMappings(domain?.id || null, isOpen && !!domain?.id);
  const { data: gameProvidersData } = useGameProviders();
  const [gameIds, setGameIds] = useState<string[]>([]);

  useEffect(() => {
    if (domain) {
      setForm({
        type: domain.type,
        ipWhitelist: domain.ipWhitelist,
        isActive: domain.isActive,
        label: domain.label,
      });
      setUseWhitelist(domain.ipWhitelist !== null);
      setIpInput('');
    }
  }, [domain]);

  useEffect(() => {
    setGameIds(mappingsData?.data?.game_ids || []);
  }, [mappingsData?.data?.game_ids]);

  useEffect(() => {
    setGameMode((mappingsData?.data?.game_ids || []).length > 0 ? 'selected' : 'all');
  }, [mappingsData?.data?.game_ids]);

  if (!isOpen || !domain) return null;

  const handleAddIp = () => {
    const ip = ipInput.trim();
    if (!ip) return;
    const list = form.ipWhitelist ?? [];
    if (!list.includes(ip)) {
      setForm({ ...form, ipWhitelist: [...list, ip] });
    }
    setIpInput('');
  };

  const handleRemoveIp = (ip: string) => {
    setForm({ ...form, ipWhitelist: (form.ipWhitelist ?? []).filter(i => i !== ip) });
  };

  const handleToggleWhitelist = (checked: boolean) => {
    setUseWhitelist(checked);
    setForm({ ...form, ipWhitelist: checked ? (form.ipWhitelist ?? []) : null });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateDomain.mutateAsync({
        id: domain.id,
        data: {
          ...form,
          label: form.label?.trim() || null,
          ipWhitelist: useWhitelist ? (form.ipWhitelist ?? []) : null,
        },
      });
      await replaceMappings.mutateAsync({
        domainId: domain.id,
        gameIds: gameMode === 'all' ? [] : gameIds,
      });
      onSuccess();
      onClose();
    } catch {
      // error handled by hook
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-semibold">Edit Domain</CardTitle>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4 font-mono">{domain.domain}</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Label</label>
              <Input
                value={form.label ?? ''}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Kiosk #1 - Main Hall"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as DomainType })}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                >
                  <option value="kiosk">Kiosk</option>
                  <option value="mobile">Mobile</option>
                  <option value="admin">Admin / Portal</option>
                </select>
              </div>

              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="isActiveEdit"
                  checked={form.isActive ?? true}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="h-4 w-4"
                />
                <label htmlFor="isActiveEdit" className="text-sm font-medium">Active</label>
              </div>
            </div>

            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Game policy</p>
                  <p className="text-xs text-muted-foreground">
                    Optional. Empty policy means all games.
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowPolicy((v) => !v)}>
                  {showPolicy ? 'Hide' : 'Configure'}
                </Button>
              </div>
              {!showPolicy ? (
                <p className="text-xs text-muted-foreground">
                  Effective policy:{' '}
                  <span className="font-medium text-foreground">
                    {gameIds.length > 0 ? `${gameIds.length} selected games` : 'All games'}
                  </span>
                  .
                </p>
              ) : (
                <>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Game policy</label>
                    <div className="flex gap-4 text-sm">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={gameMode === 'all'}
                          onChange={() => {
                            setGameMode('all');
                            setGameIds([]);
                          }}
                        />
                        <span>All games (default)</span>
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={gameMode === 'selected'}
                          onChange={() => setGameMode('selected')}
                        />
                        <span>Selected games only</span>
                      </label>
                    </div>
                    {gameMode === 'selected' && (
                      <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-2">
                        {(gameProvidersData?.data?.items || []).map((gp) => {
                          const checked = gameIds.includes(gp.id);
                          return (
                            <label key={gp.id} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  setGameIds((prev) => (
                                    e.target.checked ? [...prev, gp.id] : prev.filter((id) => id !== gp.id)
                                  ));
                                }}
                              />
                              <span>{gp.name} ({gp.code})</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                    {gameMode === 'selected' && gameIds.length === 0 && (
                      <p className="text-xs text-amber-700">
                        No game selected. This will fallback to all games.
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>

            {form.type === 'kiosk' && (
              <div className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Network restrictions</p>
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowAdvancedNetwork((v) => !v)}>
                    {showAdvancedNetwork ? 'Hide' : 'Configure'}
                  </Button>
                </div>
                {showAdvancedNetwork && (
                  <>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="useWhitelistEdit"
                        checked={useWhitelist}
                        onChange={(e) => handleToggleWhitelist(e.target.checked)}
                        className="h-4 w-4"
                      />
                      <label htmlFor="useWhitelistEdit" className="text-sm font-medium">
                        Enable IP Whitelist
                      </label>
                    </div>
                    {!useWhitelist && (
                      <p className="text-xs text-muted-foreground">All IPs are allowed when whitelist is disabled.</p>
                    )}
                    {useWhitelist && (
                      <>
                        <div className="flex gap-2">
                          <Input
                            value={ipInput}
                            onChange={(e) => setIpInput(e.target.value)}
                            placeholder="192.168.1.100"
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddIp(); } }}
                          />
                          <Button type="button" variant="outline" size="sm" onClick={handleAddIp}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="space-y-1">
                          {(form.ipWhitelist ?? []).map((ip) => (
                            <div key={ip} className="flex items-center justify-between bg-accent/50 px-3 py-1.5 rounded text-sm font-mono">
                              <span>{ip}</span>
                              <button type="button" onClick={() => handleRemoveIp(ip)} className="text-destructive hover:text-destructive/80">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                          {(form.ipWhitelist ?? []).length === 0 && (
                            <p className="text-xs text-muted-foreground">No IPs added — all requests will be blocked.</p>
                          )}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={updateDomain.isLoading}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateDomain.isLoading || replaceMappings.isLoading}>
                {updateDomain.isLoading || replaceMappings.isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default UpdateDomainModal;
